/**
 * Recipe book.
 *
 * More than documentation: clicking an entry lays the recipe out in the
 * crafting grid, taking the items from the inventory. That is the difference
 * between a wiki page and a tool - you never have to remember a pattern.
 *
 * Entries you cannot afford are dimmed rather than hidden, so the book also
 * answers "what could I make if I went and dug some more".
 */

import { pick, ui, type Lang } from "../core/text/lang.js";
import {
  RECIPES, recipesOn,
  canCraft, fillGrid, addBatchToGrid, matchRecipe, recipeName, recipeCost,
  ingredientItems, ingredientLabel, recipeBlocked, STATION_LABELS,
  type Recipe, type Ingredient, type Station,
} from "../core/crafting/recipes.js";
import type { Inventory } from "../core/inventory/inventory.js";
import { itemTile, itemLabel, itemHint } from "../core/block/items.js";
import { Tooltip } from "./tooltip.js";
import { ATLAS_TILES } from "../core/block/tiles.js";
import { buildAtlasCanvas } from "../render/atlas.js";

const ICON_PX = 30;

/** Where the filter's setting is kept, so it survives a reload. */
const ONLY_KEY = "vyzhyvannia.book.only";

export class RecipeBook {
  private readonly rows = new Map<string, HTMLElement>();
  /** The list the rows live in, so they can be put in order again. */
  private listEl!: HTMLDivElement;
  private readonly inventory: Inventory;
  private readonly grid: Inventory;
  private readonly atlasUrl: string;

  /** Fired after a recipe has been laid into the grid. */
  onFill: ((recipe: Recipe) => void) | null = null;

  /** Crafting stations within reach, so rows can say what they still need. */
  private stations: ReadonlySet<Station> = new Set();

  private readonly tooltip: Tooltip;

  /** Show only what can be made right now. Off until somebody ticks it. */
  private only = false;
  private readonly onlyBox: HTMLInputElement;
  private readonly onlyText: HTMLElement;
  private readonly emptyEl: HTMLElement;

  /**
   * Which language the rows are written in.
   *
   * A field the owner sets rather than an argument threaded through every
   * method: the book is a long-lived object that redraws itself, and the
   * language is a property of the game, not of one call.
   */
  lang: Lang = "uk";

  constructor(
    inventory: Inventory, grid: Inventory, root: HTMLElement, tooltip: Tooltip,
  ) {
    this.inventory = inventory;
    this.grid = grid;
    this.tooltip = tooltip;
    this.atlasUrl = buildAtlasCanvas().toDataURL();

    const title = document.createElement("div");
    title.className = "book-title";
    title.textContent = "Книга рецептів";

    /*
     * A tick over the book: only what can be made right now.
     *
     * The book has always dimmed what you cannot afford rather than hiding
     * it, because it doubles as the answer to "what could I make if I went
     * and dug some more". That is worth keeping, and it is also sixty-odd
     * recipes to read past when the question is the other one: "what can I
     * make with what is in my bag". So both, and a tick to say which.
     *
     * "Right now" means right now: a рецепт that wants a піч you are not
     * standing at is not something you can make, however full the bag is.
     */
    const only = document.createElement("label");
    only.className = "book-only";
    this.onlyBox = document.createElement("input");
    this.onlyBox.type = "checkbox";
    let kept: string | null = null;
    try {
      kept = localStorage.getItem(ONLY_KEY);
    } catch {
      // Private browsing, or storage turned off. The tick simply starts off.
    }
    this.only = kept === "1";
    this.onlyBox.checked = this.only;
    this.onlyText = document.createElement("span");
    only.append(this.onlyBox, this.onlyText);
    this.onlyBox.addEventListener("change", () => {
      this.only = this.onlyBox.checked;
      try {
        localStorage.setItem(ONLY_KEY, this.only ? "1" : "0");
      } catch {
        // Then it lasts as long as the page does, which is still worth having.
      }
      this.refresh();
    });

    const list = document.createElement("div");
    list.className = "recipe-list";
    this.listEl = list;

    for (const recipe of this.onThisMap()) {
      list.appendChild(this.makeRow(recipe));
    }

    /*
     * And a line for when the tick leaves nothing.
     *
     * An empty list with no explanation reads as a broken book - this is the
     * one state where hiding rows can hide all of them.
     */
    this.emptyEl = document.createElement("div");
    this.emptyEl.className = "recipe-empty";
    this.emptyEl.hidden = true;
    list.append(this.emptyEl);

    const hint = document.createElement("div");
    hint.className = "inv-hint";
    hint.textContent =
      "Клацни рецепт — розкладеться в сітці. Клацай ще — більше за раз";

    root.append(title, only, list, hint);
    this.retitle();
  }

  /** Write the filter's words again, in the language now in force. */
  retitle(): void {
    this.onlyText.textContent = ui("book-only", this.lang);
    this.emptyEl.textContent = ui("book-none", this.lang);
  }

  private icon(id: number, size = ICON_PX, ingredient?: Ingredient): HTMLElement {
    const el = document.createElement("div");
    el.className = "recipe-icon";
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    const tile = itemTile(id);
    const sheet = size * ATLAS_TILES;
    el.style.backgroundImage = `url(${this.atlasUrl})`;
    el.style.backgroundSize = `${sheet}px ${sheet}px`;
    el.style.backgroundPosition =
      `${-(tile % ATLAS_TILES) * size}px ${-Math.floor(tile / ATLAS_TILES) * size}px`;
    // How many of this the player has, so the book answers "what am I short
    // of" without making them go and count.
    this.tooltip.attach(el, () => {
      // For a tag, count everything that satisfies it, not just the icon shown.
      const items = ingredient === undefined ? [id] : ingredientItems(ingredient);
      const have = items.reduce((n, i) => n + this.inventory.countOf(i), 0);
      const title = ingredient === undefined ? itemLabel(id) : ingredientLabel(ingredient);
      const hint = itemHint(id);
      return {
        title,
        hint: hint !== undefined ? `${hint} · маєш ${have}` : `Маєш ${have}`,
      };
    });
    return el;
  }

  private makeRow(recipe: Recipe): HTMLElement {
    const row = document.createElement("button");
    row.type = "button";
    row.className = "recipe-row";
    // Named in the DOM, so a check can tell a row that moved from one that
    // was always there.
    row.dataset.recipe = recipe.id;

    const inputs = document.createElement("div");
    inputs.className = "recipe-inputs";
    for (const { ingredient, count } of recipeCost(recipe)) {
      const group = document.createElement("div");
      group.className = "recipe-ingredient";
      // A tag shows the first thing that satisfies it; the tooltip names the
      // tag itself, so "будь-які дошки" is not hidden behind an oak icon.
      group.append(this.icon(ingredientItems(ingredient)[0] ?? 0, ICON_PX, ingredient));
      const n = document.createElement("span");
      n.textContent = `×${count}`;
      group.append(n);
      inputs.append(group);
    }

    const arrow = document.createElement("span");
    arrow.className = "recipe-arrow";
    arrow.textContent = "→";

    const output = document.createElement("div");
    output.className = "recipe-output";
    output.append(this.icon(recipe.result.id, 36));
    const label = document.createElement("div");
    label.className = "recipe-label";
    label.textContent = `${recipeName(recipe, this.lang)} ×${recipe.result.count}`;
    output.append(label);

    const text = document.createElement("div");
    text.className = "recipe-note";
    text.textContent = recipe.note === undefined ? "" : pick(recipe.note, this.lang);

    // A badge, so "needs a fire" is visible without reading the note.
    if (recipe.requires !== undefined) {
      const badge = document.createElement("span");
      badge.className = "recipe-station";
      badge.textContent = pick(STATION_LABELS[recipe.requires], this.lang);
      output.append(badge);
    }

    const main = document.createElement("div");
    main.className = "recipe-main";
    main.append(inputs, arrow, output);

    row.append(main, text);
    row.addEventListener("click", () => {
      /*
       * First click lays the recipe out; every click after it adds a course.
       *
       * The grid crafts once per course, so stacking it is how you make ten
       * of something without ten trips to the book. A click that cannot
       * afford another course does *nothing* - it must never fall through to
       * `fillGrid`, which tips the grid back into the bag and lays out one,
       * and would therefore undo the stack the player had just built.
       *
       * Laying it out is still allowed without the fire - you can see what
       * it wants, and the result slot explains why nothing comes of it.
       */
      if (matchRecipe(this.grid, this.mapId)?.id === recipe.id) {
        if (addBatchToGrid(recipe, this.inventory, this.grid)) {
          this.onFill?.(recipe);
        }
        return;
      }
      if (!canCraft(recipe, this.inventory)) return;
      if (fillGrid(recipe, this.inventory, this.grid)) this.onFill?.(recipe);
    });

    this.rows.set(recipe.id, row);
    return row;
  }

  /** Which map this is: a recipe that belongs to another is not in the book. */
  /** The list this book draws: everything, less what belongs to other maps. */
  private onThisMap(): readonly Recipe[] {
    return this.mapId.length === 0 ? RECIPES : recipesOn(this.mapId);
  }

  setMap(mapId: string): void {
    this.mapId = mapId;
    this.refresh();
  }

  private mapId = "";

  setStations(stations: ReadonlySet<Station>): void {
    this.stations = stations;
    this.refresh();
  }

  /**
   * Dim what cannot currently be made, and put what can at the top.
   *
   * Call whenever the inventory changes. "Зроби щоб у книзі рецептів ті,
   * які доступні, переносились вгору": the book is opened to find out what
   * you can make *now*, and reading past forty dimmed rows to find the two
   * bright ones is the book getting in the way of that. Within each half
   * the order is the book's own, so a recipe does not jump about as things
   * come and go - only between the halves.
   *
   * Moved, not rebuilt: appending a row that is already in the list moves
   * it, so the rows keep their identity and their listeners.
   */
  refresh(): void {
    let canMake = 0;
    const can: HTMLElement[] = [];
    const cannot: HTMLElement[] = [];
    for (const recipe of this.onThisMap()) {
      const row = this.rows.get(recipe.id);
      if (row === undefined) continue;
      const short = !canCraft(recipe, this.inventory);
      const noFire = recipeBlocked(recipe, this.stations) !== null;
      const out = short || noFire;
      row.classList.toggle("unaffordable", out);
      row.classList.toggle("needs-station", noFire);
      // Hidden rather than dimmed, when the tick is on.
      row.hidden = this.only && out;
      if (!out) canMake++;
      (out ? cannot : can).push(row);
    }
    this.listEl.append(...can, ...cannot, this.emptyEl);
    this.emptyEl.hidden = !this.only || canMake > 0;
  }

}
